import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";

import { errorCallback } from "./error";

import {
    IInstitutionCreateBody,
    IInstitutionUpdateBody,
    IInstitutionStatusBody,
    IInstitutionRenewBody,
    IInstitutionDeviceStatusBody,
    IInstitutionResetPasswordBody,
} from "@/api/types/institution";
import {
    GetInstitutions,
    GetExpiringInstitutions,
    GetInstitutionById,
    CreateInstitution,
    UpdateInstitution,
    DeleteInstitution,
    UpdateInstitutionStatus,
    RenewInstitutionContract,
    ResetInstitutionPassword,
    GetInstitutionDevices,
    UpdateInstitutionDeviceStatus,
    RemoveInstitutionDevice,
} from "@/api/services/institution";

export function useInstitutionList() {
    return useQuery({
        queryKey: ["Institutions"],
        queryFn: () => GetInstitutions(),
    });
}

export function useExpiringInstitutions() {
    return useQuery({
        queryKey: ["Institutions", "Expiring"],
        queryFn: () => GetExpiringInstitutions(),
    });
}

export function useInstitutionById(id: number) {
    return useQuery({
        enabled: !!id,
        queryKey: ["Institution", id],
        queryFn: () => GetInstitutionById(Number(id)),
    });
}

export function useCreateInstitution() {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: (body: IInstitutionCreateBody) => CreateInstitution(body),
        onError: errorCallback,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["Institutions"] });
            enqueueSnackbar("Institution created successfully", { variant: "success" });
        },
    });
}

export function useUpdateInstitution() {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: ({ id, body }: { id: number, body: IInstitutionUpdateBody }) => UpdateInstitution(id, body),
        onError: errorCallback,
        onSuccess: (data, { id }) => {
            enqueueSnackbar(`The institution ${data.data.name} has been updated successfully`, { variant: "success" });
            queryClient.invalidateQueries({ queryKey: ["Institutions"] });
            queryClient.invalidateQueries({ queryKey: ["Institution", id] });
        },
    });
}

export function useDeleteInstitution() {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: (id: number) => DeleteInstitution(id),
        onError: errorCallback,
        onSuccess: (data) => {
            enqueueSnackbar(`${data.data.result}`, { variant: "success" });
            queryClient.invalidateQueries({ queryKey: ["Institutions"] });
        },
    });
}

export function useUpdateInstitutionStatus() {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: ({ id, body }: { id: number, body: IInstitutionStatusBody }) => UpdateInstitutionStatus(id, body),
        onError: errorCallback,
        onSuccess: (data, { id }) => {
            enqueueSnackbar(`Institution status updated to ${data.data.status}`, { variant: "success" });
            queryClient.invalidateQueries({ queryKey: ["Institutions"] });
            queryClient.invalidateQueries({ queryKey: ["Institution", id] });
        },
    });
}

export function useRenewInstitutionContract() {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: ({ id, body }: { id: number, body: IInstitutionRenewBody }) => RenewInstitutionContract(id, body),
        onError: errorCallback,
        onSuccess: (data, { id }) => {
            enqueueSnackbar("Institution contract renewed successfully", { variant: "success" });
            queryClient.invalidateQueries({ queryKey: ["Institutions"] });
            queryClient.invalidateQueries({ queryKey: ["Institution", id] });
        },
    });
}

export function useResetInstitutionPassword() {
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: ({ id, body }: { id: number, body: IInstitutionResetPasswordBody }) =>
            ResetInstitutionPassword(id, body),
        onError: errorCallback,
        onSuccess: (data) => {
            enqueueSnackbar(`${data.data.result}`, { variant: "success" });
        },
    });
}

export function useInstitutionDevices(institutionId: number) {
    return useQuery({
        enabled: !!institutionId,
        queryKey: ["InstitutionDevices", institutionId],
        queryFn: () => GetInstitutionDevices(Number(institutionId)),
    });
}

export function useUpdateInstitutionDeviceStatus() {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: ({ id, deviceId, body }: { id: number, deviceId: number, body: IInstitutionDeviceStatusBody }) =>
            UpdateInstitutionDeviceStatus(id, deviceId, body),
        onError: errorCallback,
        onSuccess: (data, { id }) => {
            enqueueSnackbar(`Device status updated to ${data.data.status}`, { variant: "success" });
            queryClient.invalidateQueries({ queryKey: ["InstitutionDevices", id] });
        },
    });
}

export function useRemoveInstitutionDevice() {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: ({ id, deviceId }: { id: number, deviceId: number }) => RemoveInstitutionDevice(id, deviceId),
        onError: errorCallback,
        onSuccess: (data, { id }) => {
            enqueueSnackbar(`${data.data.result}`, { variant: "success" });
            queryClient.invalidateQueries({ queryKey: ["InstitutionDevices", id] });
        },
    });
}
